import React, { ChangeEventHandler, FC, useEffect, useState } from 'react';
import Select from 'react-select';
import { useExaminationGrades } from '@components/profile/profileOverlay/overlays/skill/query/useExaminations';
import { ReactSelectStyles } from '@components/profile/profileOverlay/overlays/ReactSelectStyles';

import styles from '../../skill.module.scss';
import { ExaminationVO } from '@domain/vo/profile/ProfileSkillVO';
import { ExaminationDto, ExaminationGradeDto } from '@domain/rs/profile/skill/ExaminationRs';
import { onlyNumber } from '@utils/validators/validators';
import classnames from 'classnames/bind';
const cx = classnames.bind(styles);

interface IGradesSelectProps {
  examination: ExaminationDto;
  onAppend: (selected: ExaminationVO) => void;
}

const GradesSelect: FC<IGradesSelectProps> = ({ examination, onAppend }) => {
  const [gradeOptions, setGradeOptions] = useState<ExaminationGradeDto[]>([]);

  const { mutateAsync: examinationGradesMutateAsync } = useExaminationGrades();

  useEffect(() => {
    if (examination) {
      examinationGradesMutateAsync(examination.code).then((grades) => {
        setGradeOptions(grades);
      });
    }
  }, [examination]);

  const onSelectGrade = (selected: any) => {
    const item = selected as ExaminationGradeDto;

    onAppend(
      new ExaminationVO({
        ...examination,
        gradeCode: item.code,
        grade: item.grade,
        score: null,
      }),
    );
  };

  return (
    <Select
      className={cx('select')}
      placeholder={'점수'}
      getOptionValue={(option) => option.code}
      getOptionLabel={(option) => option.grade}
      styles={ReactSelectStyles}
      isSearchable={false}
      options={gradeOptions}
      onChange={onSelectGrade}
    />
  );
};

interface IScoreInputProps {
  examination: ExaminationDto;
  onAppend: (selected: ExaminationVO) => void;
}

const ScoreInput: FC<IScoreInputProps> = ({ examination, onAppend }) => {
  const [score, setScore] = useState('');

  const onKeyDownInput = (e: any) => {
    const enterKeys = ['Enter', 'NumpadEnter'];
    if (enterKeys.includes(e.code)) {
      onAppend(
        new ExaminationVO({
          ...examination,
          gradeCode: null,
          grade: null,
          score: Number(score),
        }),
      );
      return;
    }
  };

  const onChangeInput: ChangeEventHandler<HTMLInputElement> = (e) => {
    //점수 0 ~ 9999점까지 입력 가능
    if (Number(e.target.value) > 9999) return;
    setScore(e.target.value);
  };

  const onBlurInput = () => {
    onAppend(
      new ExaminationVO({
        ...examination,
        gradeCode: null,
        grade: null,
        score: Number(score),
      }),
    );
  };

  return (
    <input
      className={cx('input')}
      type={'text'}
      placeholder={'점수'}
      onKeyDown={onKeyDownInput}
      onChange={onChangeInput}
      value={score}
      onInput={onlyNumber}
      onBlur={onBlurInput}
    />
  );
};

const DefaultSelect = () => {
  return <Select className={cx('select')} placeholder={'점수'} styles={ReactSelectStyles} isDisabled={true} />;
};

interface IScoreGradeContentsProps {
  examination: ExaminationDto | null;
  onAppend: (selected: ExaminationVO) => void;
}

const ScoreGradeContents: FC<IScoreGradeContentsProps> = ({ examination, onAppend }) => {
  if (!examination) return <DefaultSelect />;

  if (examination.scoreYn) return <ScoreInput examination={examination} onAppend={onAppend} />;

  return <GradesSelect examination={examination} onAppend={onAppend} />;
};

export default ScoreGradeContents;
